import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { financeApi } from '../../services/financeApi.js';
import { formatCurrency } from '../../utils/formatters.js';

export function DisposeAssetModal({ asset, onClose }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [disposedDate, setDisposedDate] = useState('');
  const [proceeds, setProceeds] = useState('0');
  const [error, setError] = useState(null);

  useEffect(() => {
    if (asset) { setDisposedDate(''); setProceeds('0'); setError(null); }
  }, [asset]);

  const dispose = useMutation({
    mutationFn: (body) => financeApi.disposeFixedAsset(asset.id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['finance', 'fixed-asset-register'] });
      queryClient.invalidateQueries({ queryKey: ['finance', 'fixed-asset-insights'] });
      onClose();
    },
    onError: (err) => setError(err?.message || t('common.loadError')),
  });

  if (!asset) return null;

  const nbv = Number(asset.netBookValue) || 0;
  const diff = (Number(proceeds) || 0) - nbv;
  const isGain = diff >= 0;

  function submit(e) {
    e.preventDefault();
    setError(null);
    dispose.mutate({ disposedDate, proceeds: proceeds || '0' });
  }

  return (
    <Modal
      open={Boolean(asset)}
      onClose={onClose}
      title={t('fa.dispose.title', { name: asset.name })}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="danger" onClick={submit} disabled={dispose.isPending || !disposedDate}>
            {dispose.isPending ? `${t('fa.dispose.confirm')}…` : t('fa.dispose.confirm')}
          </Button>
        </>
      }
    >
      <form onSubmit={submit} className="form-stack">
        <div className="field-row">
          <label className="field">
            <span className="field-label">{t('fa.dispose.date')}</span>
            <input className="input" type="date" value={disposedDate} onChange={(e) => setDisposedDate(e.target.value)} required />
          </label>
          <label className="field">
            <span className="field-label">{t('fa.dispose.proceeds')}</span>
            <input className="input" type="number" min="0" step="0.01" value={proceeds} onChange={(e) => setProceeds(e.target.value)} />
          </label>
        </div>
        <div className="alert info">
          {t('fa.dispose.nbv', { amount: formatCurrency(nbv) })}
          {' · '}
          {isGain
            ? t('fa.dispose.gain', { amount: formatCurrency(diff) })
            : t('fa.dispose.loss', { amount: formatCurrency(-diff) })}
        </div>
        {error && <div className="alert danger">{error}</div>}
      </form>
    </Modal>
  );
}
